import { IntEnumConditions, StringConditions } from '../../../grid/gridFilters/types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { Feature } from '@nirvana/shared/constants';

export enum RpcExclusionItemsMethod {
  REMOVE_RECORDS = "removeRecords",
  UPDATE_RECORDS = "updateRecords",
}

export enum RpcExclusionItemToListMappings {
  ASSIGN_ITEM_TO_MULTIPLE_LISTS = "assignItemToMultipleLists",
  ASSIGN_MULTIPLE_ITEMS_TO_MULTIPLE_LISTS = "assignMultipleItemsToMultipleLists",
}

export enum RpcAction {
  EXCLUSION_ITEMS = "ExclusionItems",
  EXCLUSION_ITEM_TO_LIST_MAPPINGS = "ExclusionItemToListMappings",
}

export enum Column {
  OBJECT_TYPE,
  EXCLUDED_ITEMS,
  MODULES,
  REMARKS,
  IN_LISTS,
  IN_POLICIES,
  ADDED_ON,
}

export enum ColumnKeys {
  OBJECT_TYPE = 'objectType',
  EXCLUDED_ITEMS = 'excludedItems',
  MODULES = 'modules',
  REMARKS = 'remarks',
  IN_LISTS = 'inLists',
  IN_POLICIES = 'inPolicies',
  ADDED_ON = 'addedOn',
}

export const FILTERS = [{ objectType: { in: [] } }, { excludedItems: { contains: '' } }, { modules: { in: [] } }];

export const COLUMNS = [
  Column.OBJECT_TYPE,
  Column.EXCLUDED_ITEMS,
  Column.MODULES,
  Column.REMARKS,
  Column.IN_LISTS,
  Column.ADDED_ON,
];

export const PREDEFINED_FILTERS_KEYS = [ColumnKeys.OBJECT_TYPE, ColumnKeys.EXCLUDED_ITEMS, ColumnKeys.MODULES];

export interface Filter {
  objectType?: IntEnumConditions;
  excludedItems?: StringConditions;
  modules?: IntEnumConditions;
  remarks?: StringConditions;
  //inLists?: StringConditions;
  //inPolicies?: StringConditions;
}

export enum ObjectType {
  FILE = 1,
  FOLDER = 2,
  FILE_EXTENSION = 3,
  PROCESS = 4,
  FILE_HASH = 5,
  CERTIFICATE_HASH = 6,
  THREAT_NAME = 7,
  CMD_PARAMS = 8,
  IP_MASK = 9,
  CMD_PARAMS_REGEX = 10,
}

export enum Module {
  ON_DEMAND = 1,
  ON_ACCESS = 2,
  ATC_IDS = 4,
  RANSOMWARE_MITIGATION = 6,
  LSASS_PROTECTION = 7,
  DPI_DETECTION = 8,
  ENTIRE_AAE = 9,
  SENSITIVE_REGISTRY_PROTECTION = 10,
}

export interface ExclusionItem {
  type: Number,
  path: string,
  modules: Array<number>
}

export interface List {
  label: string;
  value: string;
  description?: string;
  endpointsCount?: number;
}

export interface DataGridItemList {
  id: string;
  name: string;
  policies: Array<DataGridItemListPolicy>;
}

export interface DataGridItemListPolicy {
  id: string;
  name: string;
}

export enum ExclusionListTypes {
  ANTIMALWARE = 1,
}

export interface DataGridItem {
  objectType?: ObjectType;
  excludedItems?: string;
  modules?: string;
  remarks?: string;
  inLists?: Array<DataGridItemList>;
  //inPolicies?: Array<string>;
  addedOn?: string;
}

export interface GetDataGridItemsResponse {
  rawEvents?: Array<DataGridItem>;
}

export interface SetFiltersRequest {
  viewId: string;
  filters: Array<Filter>;
}

export interface ExclusionListItem {
  label: string;
  value: string;
  refresh?: boolean;
}

export interface ModuleConfig {
  label: string;
  value: Module;
  disabled?: boolean;
  selected?: boolean;
  parent?: string;
}

export interface TooltipConfig {
  text: string;
  value: ObjectType;
}

export interface ObjectTypeConfig {
  label: string;
  value: ObjectType;
  disabled?: boolean;
  selected?: boolean;
}

export interface ToastActionConfig {
  actionButtonText: string;
  actionCallback: EventListenerOrEventListenerObject;
}

export enum GlobalExclusionsEvents {
  CREATE_NEW_EXCLUSION_LIST = 'createNewExclusionList',
  CREATE_NEW_EXCLUSION_LIST_SUCCESS = 'createNewExclusionListSuccess',
  SHOW_EXCLUSION_LIST = 'showExclusionList',
  SHOW_ALL_EXCLUSION_LIST = 'showAllExclusions',
  DELETE_EXCLUSION_ITEM = 'deleteExclusion',
  CONFIRM_DELETE_EXCLUSION_ITEM = 'confirmDeleteExclusion',
  ASSIGN_EXCLUSION_ITEM = 'assignExclusion',
  DELETE_EXCLUSION_LIST = 'deleteExclusionList',
  ADD_EXCLUSIONS = 'addExclusions',
  ASSIGN_LIST_TO_POLICIES = 'assignToMultiplePolicies',
  REFRESH_COMPONENT = 'refreshComponent',
  LINK_TITLE_CLICK = 'gridLinkTitleClick',
  SHOW_EXPORT_EXCLUSIONS = 'showExportExclusions',
  ASSIGN_ITEMS_TO_LISTS = 'assignItemsToLists',
}

export enum ExclusionItemsActions {
  DELETE_EXCLUSION = 'deleteExclusion',
  EDIT_EXCLUSION = 'editExclusion',
  ASSIGN_EXCLUSION = 'assignExclusion',
}

export enum ExclusionItemsBulkActions {
  EXPORT_EXCLUSIONS = 'exportExclusions',
  DELETE_EXCLUSIONS = 'deleteExclusions',
}

export function getObjectTypeValues(): Array<ObjectTypeConfig> {
  return [
    {
      label: translateFn('File'),
      value: ObjectType.FILE,
    },
    {
      label: translateFn('Folder'),
      value: ObjectType.FOLDER,
    },
    {
      label: translateFn('Extension'),
      value: ObjectType.FILE_EXTENSION,
    },
    {
      label: translateFn('Process'),
      value: ObjectType.PROCESS,
    },
    {
      label: translateFn('File Hash'),
      value: ObjectType.FILE_HASH,
    },
    {
      label: translateFn('Certificate Hash'),
      value: ObjectType.CERTIFICATE_HASH,
    },
    {
      label: translateFn('Threat Name'),
      value: ObjectType.THREAT_NAME,
    },
    {
      label: translateFn('Command Line'),
      value: ObjectType.CMD_PARAMS,
    },
    {
      label: translateFn('Command line with regex'),
      value: ObjectType.CMD_PARAMS_REGEX,
    },
    {
      label: translateFn('IP/mask'),
      value: ObjectType.IP_MASK,
    },
  ];
}

export function getModulesValues(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getFolderModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      selected: true,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      selected: true,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      selected: true,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getFileModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      selected: true,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      selected: true,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getFileExtensionModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      selected: true,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      selected: true,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getProcessModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      disabled: true,
      selected: false,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      selected: true,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      selected: true,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getFileHashModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      selected: true,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      selected: true,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getThreatModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      selected: true,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      selected: true,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      selected: true,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      disabled: true,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getCmdModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      disabled: true,
      selected: false,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      selected: true,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      selected: true,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getIpmaskModules(): Array<ModuleConfig> {
  const modules: Array<ModuleConfig> = [
    {
      label: translateFn('On-Demand'),
      value: Module.ON_DEMAND,
      disabled: true,
      selected: false,
    },
    {
      label: translateFn('On-Access'),
      value: Module.ON_ACCESS,
      disabled: true,
      selected: false,
    },
    {
      label: translateFn('ATC/IDS'),
      value: Module.ATC_IDS,
      disabled: true,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('ATC/Sensitive registry protection'),
      value: Module.SENSITIVE_REGISTRY_PROTECTION,
      selected: false,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Ransomware Mitigation'),
      value: Module.RANSOMWARE_MITIGATION,
      selected: true,
      parent: translateFn('ON-EXECUTE'),
    },
    {
      label: translateFn('Entire AAE'),
      value: Module.ENTIRE_AAE,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
    {
      label: translateFn('LSASS Protection'),
      value: Module.LSASS_PROTECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    },
  ];

  if (Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PREMIUM_VISIBLE])) {
    modules.push({
      label: translateFn('Process Introspection'),
      value: Module.DPI_DETECTION,
      disabled: true,
      selected: false,
      parent: translateFn('ADVANCED ANTI-EXPLOIT'),
    });
  }

  return modules;
}

export function getObjectTypeModuleValues(module): Array<ModuleConfig> {
  const OBJECT_TYPE_MODULES_VALUES = {
    [ObjectType.FILE]: getFileModules(),
    [ObjectType.FOLDER]: getFolderModules(),
    [ObjectType.FILE_EXTENSION]: getFileExtensionModules(),
    [ObjectType.PROCESS]: getProcessModules(),
    [ObjectType.FILE_HASH]: getFileHashModules(),
    [ObjectType.CERTIFICATE_HASH]: getFileHashModules(),
    [ObjectType.THREAT_NAME]: getThreatModules(),
    [ObjectType.CMD_PARAMS]: getCmdModules(),
    [ObjectType.CMD_PARAMS_REGEX]: getCmdModules(),
    [ObjectType.IP_MASK]: getIpmaskModules(),
  };
  return OBJECT_TYPE_MODULES_VALUES[module];
}

export function getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
  return [
    {
      column: Column.OBJECT_TYPE,
      key: ColumnKeys.OBJECT_TYPE,
      columnName: translateFn('Object type'),
      type: ColumnType.ENUM,
      values: getObjectTypeValues(),
      removable: true,
      editingDisabled: true,
    },
    {
      column: Column.EXCLUDED_ITEMS,
      key: ColumnKeys.EXCLUDED_ITEMS,
      columnName: translateFn('Excluded items'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
    },
    {
      column: Column.MODULES,
      key: ColumnKeys.MODULES,
      columnName: translateFn('Modules'),
      type: ColumnType.ENUM,
      values: getModulesValues(),
      removable: true,
    },
    {
      column: Column.REMARKS,
      key: ColumnKeys.REMARKS,
      columnName: translateFn('Remarks'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
    },
    {
      column: Column.IN_LISTS,
      key: ColumnKeys.IN_LISTS,
      columnName: translateFn('In lists'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
      filteringDisabled: true,
      editingDisabled: true,
    },
    /*{
     column: Column.IN_POLICIES,
     key: ColumnKeys.IN_POLICIES,
     columnName: 'In policies',
     type: ColumnType.STRING,
     removable: true,
     },*/
    {
      column: Column.ADDED_ON,
      key: ColumnKeys.ADDED_ON,
      columnName: translateFn('Added on'),
      type: ColumnType.STRING,
      removable: true,
      sortable: true,
      filteringDisabled: true,
      editingDisabled: true,
    },
  ];
}

export function getObjectTypeTooltipValues(): Array<TooltipConfig> {
  return [
    {
      text: translateFn(
        'Exclusions for this object type accept question mark (?), asterisk (*) and double asterisk (**) as wildcards. Example: {EXAMPLE}', {
          EXAMPLE: "C:\\Test\\*.png",
        },
      ),
      value: ObjectType.FILE,
    },
    {
      text: translateFn(
        'Exclusions for this object type accept question mark (?), asterisk (*) and double asterisk (**) as wildcards. Example: {EXAMPLE}', {
          EXAMPLE: "**\\file.txt",
        },
      ),
      value: ObjectType.FOLDER,
    },
    {
      text: translateFn('Exclusions for this object type only accept exact matches. Example: txt;TXT'),
      value: ObjectType.FILE_EXTENSION,
    },
    {
      text: translateFn(
        'Exclusions for this object type accept question mark (?), asterisk (*) and double asterisk (**) as wildcards. Example: {EXAMPLE}', {
          EXAMPLE: "C:\\**\\Microsoft.Not??.exe",
        },
      ),
      value: ObjectType.PROCESS,
    },
    {
      text: translateFn(
        'Exclusions for this object type only accept exact matches. Example: e0d123e5f316bef78bfdf5a008837577'
      ),
      value: ObjectType.FILE_HASH,
    },
    {
      text: translateFn(
        'Exclusions for this object type only accept exact matches. Example: 752c14ea195c369bac3c3b7896975ee9fd15eeb7'
      ),
      value: ObjectType.CERTIFICATE_HASH,
    },
    {
      text: translateFn(
        'Exclusions for this object type only accept exact matches. Example: EICAR-Test-File (not a virus)'
      ),
      value: ObjectType.THREAT_NAME,
    },
    {
      text: translateFn('Exclusions for this object type only accept exact matches. Example: npx nx format'),
      value: ObjectType.CMD_PARAMS,
    },
    {
      text: translateFn('Exclusions for this object type accept regular expressions. Example: m/[^abc]/'),
      value: ObjectType.CMD_PARAMS_REGEX,
    },
    {
      text: translateFn('Exclusions for this object type only accept exact matches. Example: 192.168.1.0/24'),
      value: ObjectType.IP_MASK,
    },
  ];
}
